import { useRef } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { REPORTING_LE_LENSES_PATH } from '~/consts/reportingLeLensApi';

import { request } from '~/services/common';

import type { LensFilterValues, LensPeriod } from '~/types/leLens';

import { resolveReportingLeLensUrl } from '~/utils/resolveReportingLeLensUrl';

/** One PDF in a scheduled email: a label (it names the file) and the Lens filters it is drawn with. */
export type LensScheduleFilterSet = { label: string; filters: LensFilterValues };

export type LensScheduleRunFile = { label: string; fileName?: string; bytes?: number; error?: string };

export type LensScheduleDeliveryCounts = { pending: number; accepted: number; failed: number; unknown: number };

export type LensScheduleRun = {
  id: string;
  scheduleId: string;
  runDate: string;
  trigger: 'schedule' | 'test' | 'manual';
  status: 'queued' | 'running' | 'sent' | 'partial' | 'failed' | 'unknown';
  files: Array<LensScheduleRunFile>;
  delivery: 'attachments' | 'link' | null;
  error: string | null;
  createdAt: string;
  finishedAt: string | null;
  /** Provider acceptance is recorded separately from inbox delivery. Legacy runs may omit this. */
  deliveryCounts?: LensScheduleDeliveryCounts;
};

export type LensSchedule = {
  id: string;
  lensId: string;
  ownerEmail: string;
  name: string;
  /** Omitted by older servers: these schedules are monthly at 08:00 Sydney time. */
  recurrence?: 'daily' | 'monthly';
  sendTime?: string;
  /** 1 to 28 for monthly schedules; ignored for daily schedules. */
  dayOfMonth: number;
  /** Null sends the Lens's own default period. */
  period: LensPeriod | null;
  filterSets: Array<LensScheduleFilterSet>;
  recipients: Array<string>;
  enabled: boolean;
  /** Lets a custom view show figures it marks internal (e.g. vendor revenue). Off unless chosen. */
  includeInternal: boolean;
  recentRuns: Array<LensScheduleRun>;
};

export type LensScheduleInput = Pick<
  LensSchedule,
  | 'name'
  | 'recurrence'
  | 'sendTime'
  | 'dayOfMonth'
  | 'period'
  | 'filterSets'
  | 'recipients'
  | 'enabled'
  | 'includeInternal'
>;

const schedulesPath = (lensId: string) => `${REPORTING_LE_LENSES_PATH}/${encodeURIComponent(lensId)}/schedules`;
const schedulePath = (lensId: string, scheduleId: string) =>
  `${schedulesPath(lensId)}/${encodeURIComponent(scheduleId)}`;
const runPath = (lensId: string, runId: string) =>
  `${REPORTING_LE_LENSES_PATH}/${encodeURIComponent(lensId)}/schedule-runs/${encodeURIComponent(runId)}`;

export const lensScheduleQueryKeys = {
  list: (lensId: string) => ['reporting', 'le-lens', 'schedules', lensId] as const,
  run: (lensId: string, runId: string) => ['reporting', 'le-lens', 'schedule-run', lensId, runId] as const,
};

const json = (method: string, body?: unknown) => ({
  method,
  headers: { 'Content-Type': 'application/json' },
  ...(body === undefined ? {} : { body: JSON.stringify(body) }),
});

/** The signed-in person's own schedules on this Lens, with their latest sends. */
export function useLensSchedules(lensId: string | undefined, enabled = true) {
  return useQuery<Array<LensSchedule>>({
    queryKey: lensScheduleQueryKeys.list(lensId ?? ''),
    enabled: Boolean(lensId) && enabled,
    retry: false,
    // A send finishes in the background. Unknown submissions may still be reconciled.
    refetchInterval: (query) =>
      (query.state.data ?? []).some((schedule) =>
        schedule.recentRuns.some((run) => ['queued', 'running', 'unknown'].includes(run.status)),
      )
        ? 5000
        : false,
    queryFn: async ({ signal }) => {
      const response = (await request(resolveReportingLeLensUrl(schedulesPath(lensId!)), { signal })) as {
        schedules?: Array<LensSchedule>;
      };
      return response.schedules ?? [];
    },
  });
}

function useScheduleMutation<TInput, TResult>(lensId: string, mutationFn: (input: TInput) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSettled: () => queryClient.invalidateQueries({ queryKey: lensScheduleQueryKeys.list(lensId) }),
  });
}

export function useSaveLensSchedule(lensId: string) {
  return useScheduleMutation(lensId, async ({ id, input }: { id?: string; input: LensScheduleInput }) => {
    const response = (await request(
      resolveReportingLeLensUrl(id ? schedulePath(lensId, id) : schedulesPath(lensId)),
      json(id ? 'PUT' : 'POST', input),
    )) as { schedule: LensSchedule };
    return response.schedule;
  });
}

export function useDeleteLensSchedule(lensId: string) {
  return useScheduleMutation(lensId, async (scheduleId: string) => {
    await request(resolveReportingLeLensUrl(schedulePath(lensId, scheduleId)), { method: 'DELETE' });
  });
}

/** Keep the same request key when a response is lost; a retry must resolve the original send. */
function useQueueLensScheduleSend(lensId: string, action: 'test' | 'send-now') {
  const queryClient = useQueryClient();
  const requestIds = useRef(new Map<string, string>());
  return useScheduleMutation(lensId, async (scheduleId: string) => {
    const requestId = requestIds.current.get(scheduleId) ?? crypto.randomUUID();
    requestIds.current.set(scheduleId, requestId);
    const response = (await request(
      resolveReportingLeLensUrl(`${schedulePath(lensId, scheduleId)}/${action}`),
      json('POST', { requestId }),
    )) as { run: LensScheduleRun };
    requestIds.current.delete(scheduleId);
    // Reflect the accepted request immediately, before the list refetch completes.
    queryClient.setQueryData<Array<LensSchedule>>(lensScheduleQueryKeys.list(lensId), (schedules) =>
      schedules?.map((schedule) =>
        schedule.id === scheduleId
          ? {
              ...schedule,
              recentRuns: [response.run, ...schedule.recentRuns.filter((run) => run.id !== response.run.id)],
            }
          : schedule,
      ),
    );
    return response.run;
  });
}

/** Queue an owner-only preview without emailing the schedule's recipient list. */
export function useSendLensScheduleTest(lensId: string) {
  return useQueueLensScheduleSend(lensId, 'test');
}

/** Queue a send to every saved recipient; the server also excludes concurrent manual sends. */
export function useSendLensScheduleNow(lensId: string) {
  return useQueueLensScheduleSend(lensId, 'send-now');
}

/** One send and its files, for the link in an email whose PDFs were too large to attach. */
export function useLensScheduleRun(lensId: string, runId: string) {
  return useQuery<{ run: LensScheduleRun; schedule: { id: string; name: string } }>({
    queryKey: lensScheduleQueryKeys.run(lensId, runId),
    retry: false,
    refetchInterval: (query) =>
      ['queued', 'running', 'unknown'].includes(query.state.data?.run.status ?? '') ? 5000 : false,
    queryFn: async ({ signal }) =>
      (await request(resolveReportingLeLensUrl(runPath(lensId, runId)), { signal })) as {
        run: LensScheduleRun;
        schedule: { id: string; name: string };
      },
  });
}

/** A fresh five-minute download link for one PDF of a send. */
export async function fetchLensScheduleFileUrl(lensId: string, runId: string, fileIndex: number) {
  return (await request(resolveReportingLeLensUrl(`${runPath(lensId, runId)}/files/${fileIndex}`))) as {
    url: string;
    fileName: string;
    label: string;
  };
}
